<script lang="ts" setup>
import { ref } from "vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import Header from "@/components/organisms/common/Header.vue";
import { accountService } from "@/app/services/AccountService";
import { authService } from "@/app/services/AuthService";
import { authStore } from "@/store/auth";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import { sendAdjustEvent } from "@/plugins/adjust";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import PasswordInputGroup from "@/components/molecules/form/PasswordInputGroup.vue";
import { captureException } from "@/plugins/sentry";
import { useLoader } from "@/utils/loader";
import { useErrorModal, useModal } from "@/utils/modal";
import { useCustomRouter } from "@/utils/router";
import { AbTest3113 } from "@/app/services/AbTestService";
import { onMounted } from "vue";

const errorModal = useErrorModal();
const modal = useModal();
const loader = useLoader();
const customRouter = useCustomRouter();

const password = ref("");
const requesting = ref(false);

onMounted(() => {
  // 簡単にpasswordのinputを取れないのでqueryで取得
  const passwordInput = document.querySelector<HTMLInputElement>(
    "[type='password']"
  );
  if (passwordInput) passwordInput.focus();
});

async function nextStep() {
  try {
    // 連打防止
    if (requesting.value) return;

    // registration がコールされた後、ログインステータスの値はクリアされてしまう
    // 後続処理で利用したいので、ここで取得しておく
    const workerLoginStatus = authStore.workerLoginStatus;

    requesting.value = true;
    loader.show();
    await registration();
    AbTest3113.registerEvent(AbTest3113.eventTypes.enterPasswordNext);
    sendAdjustEvent("FINISH_INPUT_PASSWORD");
    sendFirebaseCustomEvent("finish_input_password", {
      session_id: authStore.sessionId || "unknown"
    });

    try {
      // authService.registration でユーザーレコードが生成される
      // アプリの場合、上記で生成されたユーザーレコードに起動に関する情報を記録する
      const cordova = window.cordova;
      if (cordova && authStore.user) {
        await accountService.recordAppBoot();
      }
      // ODM の初期設定
      authStore.initiateOnDeviceConversionMeasurement();
    } catch (e) {
      // アプリの挙動には影響ないため、エラー通知はするが処理は続行する
      captureException(e as Error);
    }

    // 認証済みの退会ユーザーだった場合、以降の認証処理はスキップする
    if (workerLoginStatus === "verified_leave_user") {
      await customRouter.pushIgnoreError({ name: "Search" });
      return;
    }

    await customRouter.pushIgnoreError({ name: "RegisterPersonalInfo" });
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = e as any;
    // smsSessionIdが不正な場合
    if (err.response.data.smsSessionId) {
      await modal.msgBoxOk("不正な操作です", {
        centered: true
      });
      await customRouter.pushIgnoreError({ name: "Welcome" });
      return;
    }

    // 既に登録済みの場合
    if (err.response.data.nonFieldErrors) {
      await errorModal.showErrorModal(err);
      await customRouter.pushIgnoreError({ name: "Welcome" });
      return;
    }
    // それ以外の場合
    await errorModal.showErrorModal(err);
  } finally {
    loader.hide();
    requesting.value = false;
  }
}

function registration() {
  const body = {
    smsSessionId: authStore.smsSessionId as string,
    password: password.value
  };
  // 退会ユーザーだった場合は復帰処理を行う
  if (
    ["unverified_leave_user", "verified_leave_user"].includes(
      authStore.workerLoginStatus!
    )
  ) {
    return authService.reactivateWorker(body);
  }
  return authService.registration(body);
}
</script>

<template>
  <div class="content-wrapper vh-100">
    <Header>
      <span>パスワード設定</span>
    </Header>
    <CustomValidationObserver v-slot="{ handleSubmit, valid }" tag="div">
      <BForm
        class="container"
        @submit.prevent.stop="handleSubmit(nextStep)"
        data-testid="main-content"
      >
        <main>
          <div class="description">
            パスワードを入力しましょう
          </div>
          <PasswordInputGroup
            v-model="password"
            data-testid="password"
            class="password-input"
            strict
            helpText="半角英字と数字を組み合わせて8文字以上で設定してください"
          />
          <AppButton
            block
            data-testid="submitButton"
            rounded
            @click="handleSubmit(nextStep)"
            class="next-button"
            :disabled="!valid"
          >
            設定して次へ
          </AppButton>
        </main>
      </BForm>
    </CustomValidationObserver>
    <BlockBack />
  </div>
</template>

<style lang="scss" scoped>
main {
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  margin: 80px 0 32px;
  color: #40220f;
}

.description {
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
}

.password-input {
  width: 100%;

  :deep(input) {
    height: 48px;
    font-weight: 700;
  }
}

.next-button {
  width: 100%;
  height: 48px;
}

// ABテスト用のクラス
.content-wrapper {
  background-color: #f2f2f2;

  :deep(.form-control) {
    background-color: $white;
  }

  .next-button {
    color: $white;

    &:disabled {
      background-color: #c4c4c4;
    }
  }

  :deep(.help-text) {
    font-size: 10px !important;
    text-align: center;
  }
}
</style>
